import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'
import { Icon } from '@/common/components/icon'
import * as styles from './DropdownMenu.css'

/**
 * DropdownMenu component built on Radix UI DropdownMenu primitive.
 *
 * Usage:
 * ```tsx
 * <DropdownMenu>
 *   <DropdownMenu.Trigger asChild>
 *     <Button>Options</Button>
 *   </DropdownMenu.Trigger>
 *   <DropdownMenu.Content>
 *     <DropdownMenu.Item onSelect={() => console.log('Edit')}>
 *       Edit
 *     </DropdownMenu.Item>
 *     <DropdownMenu.Item onSelect={() => console.log('Duplicate')}>
 *       Duplicate
 *     </DropdownMenu.Item>
 *     <DropdownMenu.Separator />
 *     <DropdownMenu.Item danger onSelect={() => console.log('Delete')}>
 *       Delete
 *     </DropdownMenu.Item>
 *   </DropdownMenu.Content>
 * </DropdownMenu>
 * ```
 */

// Root
type DropdownMenuProps = DropdownMenuPrimitive.DropdownMenuProps

function DropdownMenu({ children, ...props }: DropdownMenuProps) {
  return <DropdownMenuPrimitive.Root {...props}>{children}</DropdownMenuPrimitive.Root>
}

// Trigger
type DropdownMenuTriggerProps = DropdownMenuPrimitive.DropdownMenuTriggerProps

function DropdownMenuTrigger({ children, ...props }: DropdownMenuTriggerProps) {
  return <DropdownMenuPrimitive.Trigger {...props}>{children}</DropdownMenuPrimitive.Trigger>
}

// Content
type DropdownMenuContentProps = DropdownMenuPrimitive.DropdownMenuContentProps

function DropdownMenuContent({ children, align = 'start', ...props }: DropdownMenuContentProps) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        className={styles.content}
        sideOffset={4}
        align={align}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  )
}

// Item
type DropdownMenuItemProps = DropdownMenuPrimitive.DropdownMenuItemProps & {
  /** Render as destructive action (red text) */
  danger?: boolean
  /** Keyboard shortcut to display */
  shortcut?: string
}

function DropdownMenuItem({
  children,
  danger,
  shortcut,
  className,
  ...props
}: DropdownMenuItemProps) {
  return (
    <DropdownMenuPrimitive.Item
      className={clsx(styles.item, danger && styles.itemDanger, className)}
      {...props}
    >
      {children}
      {shortcut && <span className={styles.shortcut}>{shortcut}</span>}
    </DropdownMenuPrimitive.Item>
  )
}

// CheckboxItem
type DropdownMenuCheckboxItemProps = DropdownMenuPrimitive.DropdownMenuCheckboxItemProps

function DropdownMenuCheckboxItem({ children, ...props }: DropdownMenuCheckboxItemProps) {
  return (
    <DropdownMenuPrimitive.CheckboxItem className={styles.checkboxItem} {...props}>
      <DropdownMenuPrimitive.ItemIndicator className={styles.itemIndicator}>
        <Icon name="check" size={16} />
      </DropdownMenuPrimitive.ItemIndicator>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

// RadioGroup
type DropdownMenuRadioGroupProps = DropdownMenuPrimitive.DropdownMenuRadioGroupProps

function DropdownMenuRadioGroup({ children, ...props }: DropdownMenuRadioGroupProps) {
  return (
    <DropdownMenuPrimitive.RadioGroup {...props}>{children}</DropdownMenuPrimitive.RadioGroup>
  )
}

// RadioItem
type DropdownMenuRadioItemProps = DropdownMenuPrimitive.DropdownMenuRadioItemProps

function DropdownMenuRadioItem({ children, ...props }: DropdownMenuRadioItemProps) {
  return (
    <DropdownMenuPrimitive.RadioItem className={styles.radioItem} {...props}>
      <DropdownMenuPrimitive.ItemIndicator className={styles.itemIndicator}>
        <Icon name="radio_button_checked" size={16} />
      </DropdownMenuPrimitive.ItemIndicator>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
}

// Label
type DropdownMenuLabelProps = DropdownMenuPrimitive.DropdownMenuLabelProps

function DropdownMenuLabel({ children, ...props }: DropdownMenuLabelProps) {
  return (
    <DropdownMenuPrimitive.Label className={styles.label} {...props}>
      {children}
    </DropdownMenuPrimitive.Label>
  )
}

// Separator
type DropdownMenuSeparatorProps = DropdownMenuPrimitive.DropdownMenuSeparatorProps

function DropdownMenuSeparator(props: DropdownMenuSeparatorProps) {
  return <DropdownMenuPrimitive.Separator className={styles.separator} {...props} />
}

// Sub (submenu)
type DropdownMenuSubProps = DropdownMenuPrimitive.DropdownMenuSubProps

function DropdownMenuSub({ children, ...props }: DropdownMenuSubProps) {
  return <DropdownMenuPrimitive.Sub {...props}>{children}</DropdownMenuPrimitive.Sub>
}

// SubTrigger
type DropdownMenuSubTriggerProps = DropdownMenuPrimitive.DropdownMenuSubTriggerProps

function DropdownMenuSubTrigger({ children, ...props }: DropdownMenuSubTriggerProps) {
  return (
    <DropdownMenuPrimitive.SubTrigger className={styles.subTrigger} {...props}>
      {children}
      <Icon name="chevron_right" size={16} className={styles.subTriggerIcon} />
    </DropdownMenuPrimitive.SubTrigger>
  )
}

// SubContent
type DropdownMenuSubContentProps = DropdownMenuPrimitive.DropdownMenuSubContentProps

function DropdownMenuSubContent({ children, ...props }: DropdownMenuSubContentProps) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        className={styles.subContent}
        sideOffset={4}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.SubContent>
    </DropdownMenuPrimitive.Portal>
  )
}

// Compose the compound component
DropdownMenu.Trigger = DropdownMenuTrigger
DropdownMenu.Content = DropdownMenuContent
DropdownMenu.Item = DropdownMenuItem
DropdownMenu.CheckboxItem = DropdownMenuCheckboxItem
DropdownMenu.RadioGroup = DropdownMenuRadioGroup
DropdownMenu.RadioItem = DropdownMenuRadioItem
DropdownMenu.Label = DropdownMenuLabel
DropdownMenu.Separator = DropdownMenuSeparator
DropdownMenu.Sub = DropdownMenuSub
DropdownMenu.SubTrigger = DropdownMenuSubTrigger
DropdownMenu.SubContent = DropdownMenuSubContent

export { DropdownMenu }
export type {
  DropdownMenuProps,
  DropdownMenuTriggerProps,
  DropdownMenuContentProps,
  DropdownMenuItemProps,
  DropdownMenuCheckboxItemProps,
  DropdownMenuRadioGroupProps,
  DropdownMenuRadioItemProps,
  DropdownMenuLabelProps,
  DropdownMenuSeparatorProps,
  DropdownMenuSubProps,
  DropdownMenuSubTriggerProps,
  DropdownMenuSubContentProps,
}
